import _ from 'lodash'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'
import calendars from 'scoreboard/components/calendars'
import reportConstants, { labeledValue } from 'scoreboard/reports/report-constants'
import { HIDE_ON_VIEW_CLASS } from './header-cell'
import TableStack from './table-stack'
import BottomRepeatByHeaderCell from './bottom-repeat-by-header-cell'
import MiddleRepeatByHeaderCell from './middle-repeat-by-header-cell'
import TopRepeatByHeaderCell from './top-repeat-by-header-cell'
import rangeToPeriods from '../range-to-periods'
import DataType from 'scoreboard/datasets/model/data-type'
const { TIME } = DataType
import formatValue from 'scoreboard/components/format-value'
import momentHelper from 'scoreboard/components/moment-helper'
import cloneDeepWithModels from 'scoreboard/components/clone-deep-with-models'

function buildDataContext(columnsAttributes, repeatByAttributes, options) {
	let labelLimit = reportConstants.BREAKDOWN_LABEL_LIMIT
	let breakdownRows = _.map(repeatByAttributes.breakdowns, (breakdown, breakdownIndex) => {
		let breakdownLabelsWithId = breakdown.get('breakdownLabelsWithId')
		let breakdownOptions = _.defaults({ addable: false }, options)  //only field row is addable

		if (breakdown.has('range')) {
			if (_.isNil(breakdownLabelsWithId) || _.isEmpty(breakdownLabelsWithId)) {
				breakdownLabelsWithId = rangeToPeriods(breakdown.get('range'))
			}
			labelLimit = Math.min(options.rangeLimit || labelLimit, labelLimit)
			//periods within a range cannot be reordered
			breakdownOptions.preventHorizontalDrag = true
		}

		if (_.isEmpty(breakdownLabelsWithId) && breakdown.has('breakdownBy')) {
			//#38632 show "No X" when no results on the breakdown
			let referencedColumnLabel = options.reportModel.columnDataByFieldIdOrColumnType(breakdown.get('breakdownBy')).label
			breakdownLabelsWithId.push({label:i18n.get('report.designer.emptyResults.noPlaceholder', referencedColumnLabel)})
		}

		//prevent super width reports
		breakdownLabelsWithId = breakdownLabelsWithId.slice(0, labelLimit)

		return _(breakdownLabelsWithId).sortBy(({ rawValue }) => {
			// #40613 Yes before No
			switch (rawValue) {
				case 'true': case true: return 1
				case 'false': case false: return 2
				default: return 1
			}
		}).map((item, orderIndex) => {
			// most repeat by headers are formatted server side, but time is
			// always formatted on the client, do so if necessary
			const { dataset } = options.dataExtractor || {}
			const field = _.isNil(dataset) ? {} : dataset.getUnderlyingField(
				breakdown.get('breakdownBy'))
			let itemLabel = formatTimeValue(item.label, field)

			let data = { id: item.id }
			itemLabel = _.escape(i18n.get(itemLabel))
			if (_.isEmpty(item.label)) {
				//#39310 blanks in repeat column headers should be Italicized "Blank"
				itemLabel = `<span class="blank-value">${i18n.get('common.blank')}</span>`
			} else {
				itemLabel = `<span>${itemLabel}</span>`
			}
			let repeatByLabel = _.get(breakdown.attributes, 'repeatByLabel',
				options.reportModel.getDefaultRepeatByLabel(breakdown))
			data.html = labeledValue(itemLabel, repeatByLabel)

			const subtotalCalendarIds = breakdown.get('subtotalCalendarIds')
			if (breakdown.has('range') && !_.isEmpty(subtotalCalendarIds)) {
				const calendarId = _.get(calendars.periodsById[item.id],
					'calendar.id')
				data.subtotal = _.includes(subtotalCalendarIds, calendarId)
			}

			return {
				data,
				options: _.extend({
					repeatedStackCellContainer: [],
					//ensure cell row has a reference to the breakdown it represents
					breakdownReference: {
						breakdown,
						breakdownIndex
					},
					orderIndex
				} , breakdownOptions),
				//used to match the data to the correct breakdown cell
				breakdownItemId: item.id,
				type: reportConstants.HEADER_TYPE_BREAKDOWN_ROW,
				breakdownFieldId: breakdown.get('breakdownBy'),
				hideLabel: breakdown.get('hideLabel')
			}
		}).value()
	})

	let fieldRow = _.map(columnsAttributes, (attributes, orderIndex) => {
		return {
			data: attributes,
			options: _.defaults({
				orderIndex,
				repeatedStackCellContainer: []
			}, options),
			type: reportConstants.HEADER_TYPE_FIELD_ROW,
			hideLabel: attributes.hideLabel
		}
	})

	return { breakdownRows, fieldRow }
}

const formatTimeValue = (value, field) => {
	if (_.isNil(value) || _.isEmpty(field) || field?.dataType !== TIME) {
		return value
	}

	const { timeDisplayType } = field
	const timeFormat = _.isNil(timeDisplayType) ? null :
		momentHelper.getTimeFormat(timeDisplayType)
	return formatValue({ value, dataType: TIME, timeFormat })
}

/**
 * Determines the array of breakdown item ids where each element corresponds to
 * a cell in the bottom level of a multi layout.  Each element will contain the
 * firstBreakdownItemId and if breaking down by two level also the secondBreakdownItemId
 * Repeated for each field being broken down by.  For example, if we're breaking
 * down by label, with a range of the previous period to the current period and the
 * fields are the metric value and score, then given the following breakdown data
 * Label - [{id: 1, label: "Finances"}, {id:2, label:"Marketing"}]
 * Rnage - [{id:10, label: "Jan 2021"}, {id:11, label: "Feb 2021"}]
 *
 * then the breakdown item ids returned will be
 *
 * {firstBreakdownItemId: 1, secondBreakdownItemId: 10}  -> for the value field under finance -> Jan 2021
 * {firstBreakdownItemId: 1, secondBreakdownItemId: 10}  -> for the score field under finance -> Jan 2021
 * {firstBreakdownItemId: 1, secondBreakdownItemId: 11}  -> for the value field under Marketing -> Jan 2021
 * {firstBreakdownItemId: 1, secondBreakdownItemId: 11}  -> for the score field under Marketing -> Jan 2021
 * {firstBreakdownItemId: 2, secondBreakdownItemId: 10}  -> for the value field under finance -> Feb 2021
 * {firstBreakdownItemId: 2, secondBreakdownItemId: 10}  -> for the score field under finance -> Feb 2021
 * {firstBreakdownItemId: 2, secondBreakdownItemId: 11}  -> for the value field under Marketing -> Feb 2021
 * {firstBreakdownItemId: 2, secondBreakdownItemId: 11}  -> for the score field under Marketing -> Feb 2021
 */
function determineBreakdownItemIds(rowStructure) {
	let breakdownItemIds = []
	let firstBreakdownItemId
	let secondBreakdownItemId
	let onlyOneBreakdown = rowStructure.length == 2
	_.each(rowStructure[0], topRowData => {
		if (!_.isNil(topRowData.breakdownItemId)) {
			firstBreakdownItemId = topRowData.breakdownItemId
		}

		// if the top row is field data, we want to assign the breakdownItemId
		// of the next row each iteration through the next row data
		const topRowFieldData = _.isNil(firstBreakdownItemId)
		_.each(rowStructure[1], nextRowData => {
			if (!_.isNil(nextRowData.breakdownItemId)) {
				if (topRowFieldData || onlyOneBreakdown) {
					firstBreakdownItemId = nextRowData.breakdownItemId
				} else {
					secondBreakdownItemId = nextRowData.breakdownItemId
				}
			}
			if (onlyOneBreakdown) {
				//only breaking down one level (the other row represents field data)
				breakdownItemIds.push({ firstBreakdownItemId })
			} else {
				//breaking down 2 levels
				_.each(rowStructure[2], bottomRowData => {
					if (!_.isNil(bottomRowData.breakdownItemId)) {
						secondBreakdownItemId = bottomRowData.breakdownItemId
					}
					breakdownItemIds.push({ firstBreakdownItemId, secondBreakdownItemId})
				})
			}
		})
	})

   return breakdownItemIds
}


/**
 * Encapsulates all the behavior and logic related to repeat by stack.
 *
 * Each repeat by stack header consists of either 2 or 3 rows.
 *
 * Each row represents either breakdown or a column and has logic on how to
 * handle DND when dragging from and dropping over that particular cell in that
 * row.
 */
class MultiColumnTableStack extends TableStack {

	/**
	 * Constructs a MultiColumnStack for a Table
	 *
	 * @param {Integer} index the index of the stack
	 * @param {Array} columnsAttributes array of all column attributes in the stack
	 * @param {Object} repeatByAttributes the layout attributes
	 * @param {Object} opts
	 */
	constructor(index, columnsAttributes, repeatByAttributes, opts = {}) {
		super(index, columnsAttributes, opts)
		this.columns = columnsAttributes
		this.repeatBy = repeatByAttributes
		this.depth = repeatByAttributes.headerOrder.length
		this.fieldRowIndex = _.findIndex(repeatByAttributes.headerOrder, type => type == reportConstants.HEADER_TYPE_FIELD_ROW)
		this._applyLayout(columnsAttributes, repeatByAttributes)
		this._tableAvatar = [this]
	}

	_applyLayout(columnsAttributes, breakdownAttributes) {
		let { breakdownRows, fieldRow } = buildDataContext(columnsAttributes, breakdownAttributes, this.options)

		//arrange the header order
		if (breakdownAttributes.headerOrder[0] == reportConstants.HEADER_TYPE_FIELD_ROW) {
			this._rowStructure = [fieldRow].concat(breakdownRows)
		} else if (_.last(breakdownAttributes.headerOrder) == reportConstants.HEADER_TYPE_FIELD_ROW) {
			this._rowStructure = [].concat(breakdownRows, [fieldRow])
		} else {
			this._rowStructure = [].concat(breakdownRows)
			this._rowStructure.splice(1, 0, fieldRow)
		}

		//determine the colspan for each row, going from bottom to the top
		let bottomDataRow =  _.last(this._rowStructure)
		let currentSubStackLength = bottomDataRow.length
		for (let i=this._rowStructure.length-2; i>-1; i--) {
			let parentDataRow = this._rowStructure[i]
			_.each(parentDataRow, pdr => pdr.data.colspan = currentSubStackLength)
			currentSubStackLength = parentDataRow.length * currentSubStackLength
		}

		//determine which rows are currently hidden to prevent hiding all rows
		_.each(this._rowStructure, (row, rowIndex) => {
			let isHidingRow = !!row[0].hideLabel
			if (rowIndex == 0) {
				this._isHidingTopRow = isHidingRow
			} else if (_.last(this._rowStructure) === row) {
				this._isHidingBottomRow = isHidingRow
			} else {
				this._isHidingMiddleRow = isHidingRow
			}
		})

		//parse the structure of the layout
		_.each(this._rowStructure, (row, rowIndex) => {
			if (rowIndex == 0) {
				this._addTopRow(row)
			} else if (_.last(this._rowStructure) === row) {
				this._addNonTopRow(BottomRepeatByHeaderCell, row, rowIndex)
			} else {
				this._addNonTopRow(MiddleRepeatByHeaderCell, row, rowIndex)
			}
		})

		//set the cell formatters, which is responsible for generating the content
		//of each cell
		if (this.dataExtractor) {
			//the bottom row on this layout are the stacks that correspond to the data
			let bottomCells = _.last(this.rows())
			//determine the breakdown field id references for the bottom cell row
			let breakdownItemIds = determineBreakdownItemIds(this._rowStructure)
			let nextLabelIndex = 0
			let cellFormatters = _.map(bottomCells, (bottomHeaderCell) => {
				//set the breakdown column reference per cell
				bottomHeaderCell.breakdownColumnData = {
					breakdownItemId: breakdownItemIds[nextLabelIndex++]
				}
				return this.defaultCellFormatter(this.dataExtractor, bottomHeaderCell)
			})
			this.setCellFormatters(cellFormatters)
		}

		//#41551/43926 when the row is hidden and we're viewing the report (not editing)
		//we need to physically prevent this row from being included in the DOM structure
		//due to the way the table renders.  Because the table cannot be manipulated
		//when viewing, this should relatively from side effects
		//#42028 we must do this after setting the cell formatters otherwise
		//we cells will not show content due to the missing breakdownItemId
		if (!this.options.reportModel.editing) {
			const removeHiddenRow = (index) => {
				_.pullAt(this._rowStructure, index)
				_.pullAt(this._rows, index)
				this.depth-- //#42963
			}
			//order at which we check matter since we are mutating the array
			if (this._isHidingMiddleRow) {
				removeHiddenRow(1)
			}
			if (this._isHidingTopRow) {
				removeHiddenRow(0)
			}
			if (this._isHidingBottomRow) {
				removeHiddenRow(this.depth-1)
			}
		}
	}

	_allowLabelToggle(position, rowData, row) {
		if (!!rowData.hideLabel) {
			//if it's already hidden always show the toggle so it can be removed
			return true
		}
		if (row.length > 1) {
			//do not allow the label to be toggle off if more than one cell #43926
			return false
		}
		//also prevent the situation where all rows in the header can be hidden
		let otherRowsHidden
		if (position == reportConstants.REPEAT_BY_BOTTOM_POSITION) {
			otherRowsHidden = this._isHidingTopRow && (this._isHidingMiddleRow || this.depth == 2)
		} else if (position == reportConstants.REPEAT_BY_MIDDLE_POSITION) {
			otherRowsHidden = this._isHidingTopRow && this._isHidingBottomRow
		} else {
			otherRowsHidden = this._isHidingBottomRow && (this._isHidingMiddleRow || this.depth == 2)
		}
		return !otherRowsHidden
	}

	_addTopRow(row) {
		let topCells = []
		_.each(row, (rowData, cellIndex) => {
			let cellOptions = _.defaults({cellIndex, cellGroupIndex: cellIndex}, rowData.options)
			let cell = new TopRepeatByHeaderCell(this, rowData.data, this.repeatBy, cellOptions)
			cell.first = cellIndex == 0  //true if this cell is the first (left most)
			cell.last = cellIndex == (row.length-1)  //true if this cell is the last (right most)
			cell.displayLabelToggle = this._allowLabelToggle(cell.position, rowData, row)
			cell.rowData = rowData
			//#20270 add ability to hide repeated headers
			cell.style(HIDE_ON_VIEW_CLASS, !!rowData.hideLabel)
			topCells.push(cell)
		})
		this.setRow(topCells, 0)
	}

	_addNonTopRow(CellClass, row, rowIndex) {
		let cells = []
		let cellIndex = 0
		let parentCellRow = this.rows(rowIndex-1)
		_.each(parentCellRow, parentCell => {
			parentCell.options.childCells = []
			let cellGroupIndex = 0
			_.each(row, (rowData, dataIndex) => {
				let cellOptions = _.defaults({parentCell, cellIndex, cellGroupIndex}, rowData.options)
				let cell = new CellClass(this, rowData.data, this.repeatBy, cellOptions)
				//the first/last reference the first and last cell within the parent row
				cell.first = cellIndex == 0
				cell.last = cellIndex == (parentCellRow.length*row.length-1)

				//the firstChild/lastChild reference the first and last cell within this row
				cell.firstChild = dataIndex == 0
				cell.lastChild = row.length-1 == dataIndex
				cell.displayLabelToggle = this._allowLabelToggle(cell.position, rowData, row)
				cell.rowData = rowData
				//#20270 add ability to hide repeated headers
				cell.style(HIDE_ON_VIEW_CLASS, !!rowData.hideLabel)

				//ensure column reference is set on the bottom row
				if (cell.position == reportConstants.REPEAT_BY_BOTTOM_POSITION) {
					if (rowData.type != reportConstants.HEADER_TYPE_FIELD_ROW) {
						cell.column = parentCell.column
						if (parentCell.rowData.type != reportConstants.HEADER_TYPE_FIELD_ROW) {
							//column reference must be on the top level
							cell.column = parentCell.options.parentCell.column
						}
					}
				}

				parentCell.options.childCells.push(cell)
				cells.push(cell)
				cellIndex++
				cellGroupIndex++
			})
		})
		this.setRow(cells, rowIndex)
	}

	/**
	 * Informs this breakdown stack of a <TD> element that makes up row content
	 * (e.g. elements in the table body) of this breakdown stack.
	 *
	 * @param {DOMNode} td The dom node in a rows of this breakdown stack body
	 * @param {Object} cellData The data used to generate the contents of the cell
	 */
	bindStackBodyDomNode(td, cellData) {
		super.bindStackBodyDomNode(td, cellData)
		//the headers need a reference to the stack's body cell so it
		//can apply the dnd styles
		cellData.headerCell.bindStackBodyDomNode(td)

		//apply to the parent headers as well
		let parentHeaderCell = _.get(cellData.headerCell.options, 'parentCell', null)
		while (parentHeaderCell) {
			parentHeaderCell.bindStackBodyDomNode(td)
			parentHeaderCell = _.get(parentHeaderCell.options, 'parentCell', null)
		}
	}

	/**
	 * Toggles the selection state if no arguments are passed in.  Alternatively
	 * set the selected state to the given boolean and an optional breakdown cell
	 * source
	 *
	 * @param {Boolean} [state] optional state of selection, if no arguments,
	 * toggles the state from it's previous state
	 *
	 * @param {BreakdownCell} [targetCell] optional target cell of selection
	 */
	select(state = null, targetCell, opts = {}) {
		let selected = super.select(state, targetCell, opts)
		$(this.domNodes()).closest('.table').toggleClass('has-selected-stack', selected)
		if (!selected) {
			if (_.isNil(targetCell)) {
				let allSelectedClasses = ['is-selected', 'primary', 'is-draggable', 'top']
				$(this.domNodes()).toggleClass(allSelectedClasses, false)
			}
		} else if (!_.isNil(targetCell)) {
			this._applySelectedStyle(targetCell)
		}
	}

	_applySelectedStyle(targetCell) {
		let selectedNodes
		let primaryNodes
		let dragHandleClasses = ['is-draggable']

		//if there are primary nodes, put the drag handle classes there
		//otherwise they go on the first of the selected nodes
		let dragIndicatorNodes
		if (targetCell.inBreakdownRow()) {
			selectedNodes = targetCell.rowHeaderDomNodes()
			if (targetCell.position == reportConstants.REPEAT_BY_BOTTOM_POSITION) {
				primaryNodes = targetCell.rowHeaderGroupDomNodes()
			}
			dragIndicatorNodes = selectedNodes
		} else {
			selectedNodes = targetCell.repeatedColumnDomNodes()
			primaryNodes = targetCell.thisColumnDomNodes()
			dragIndicatorNodes = primaryNodes
			dragHandleClasses.push('top')
		}

		$(selectedNodes).toggleClass('is-selected', true)
		if (primaryNodes) {
			$(primaryNodes).toggleClass('primary', true)
		}
		if (targetCell.editable) {
			$(dragIndicatorNodes).first().toggleClass(dragHandleClasses)
		}
	}

	/**
	 * Returns true if this stack is allowing in the dragged cell to be
	 * dropped on the drop cell
	 */
	allowDrop(dragCell, dropCell, dropZone) {
		if (dragCell.inBreakdownRow()) {
			//check if we can reorder the breakdown items, (time periods cannot
			//be reordered)
			if (dragCell.isSameLevel(dropCell) && dragCell.options.preventHorizontalDrag) {
				return false
			}
		} else {
			//otherwise we're in a column row, determine if we're dropping outside
			//of a repeat by
			if (dropCell.stack.isRepeatByStack()) {
				if (dropCell.inBreakdownRow() && dropCell.stack !== dragCell.stack) {
					//you can't drag a column and drop it into a non column row in a
					//different stack
					return false
				}

				//https://github.com/SpiderStrategies/Scoreboard/issues/42404#issuecomment-935124535
				if (dragCell.stack.requiresTimeRange() && dropCell.stack.get('repeatBy').timeRange() == null) {
					//if the column requires a time range and the target stack doesn't
					//have one prevent the drop
					return false
				}
			} else {
				//if dropping outside of a repeatby only allow if column is allowed
				//outside of a repeat by
				if (!dragCell.column.allowedOutsideOfRepeatBy()) {
					return false
				}
			}
		}

		//the drag nodes & avatar can change based on the target drop stack
		this._applyDragStyle(dragCell, dropCell, dropZone)
		return true
	}

	/**
	 * Called on the start of the drag, performs any initialization required
	 * when a cell from this stack start a drag
	 */
	drag(dragCell, enable) {
		this._isDragging = enable
		this._applyDragStyle(dragCell, null, null)
	}

	/**
	 * Called when a entering/leaving a cell where a drop is accepted.  See base
	 * class table-stack.js for param documentation
	 */
	accept(args) {
		let {dragCell, dropCell} = args
		//style the drop target
		if (dragCell.tableStack !== dropCell.tableStack) {
			this._styleDragOriginOutsideStack(args)
		} else {
			this._styleDragOriginWithinStack(args)
		}
	}

	_styleDragOriginOutsideStack(args) {
		let {dragCell, dropCell, zone, enter} = args
		let draggingBreakdownRow = dragCell.stack.isRepeatByStack() && dragCell.inBreakdownRow()

		//if you're dragging a stack from outside a breakdown you can drop it
		//next to the breakdown or inside of it on the row containing the
		//columns (e.g. not the breakdown rows)
		let nodesOfSide = (zone) => {
			let rows = _.map(this.rows(), row => zone.left ? _.first(row) : _.last(row))
			let nodes = _(rows).map(r => r.sideDomNodes(zone.left)).flatten().value()
			return $(nodes)
		}

		let position = zone.left ? 'left' : 'right'
		let dropNodes = null
		let arrowNodes = null
		if (dropCell.inBreakdownRow() || draggingBreakdownRow) {
			//treat as dropping outside the breakdown, in that case only
			//allow dropping to the left/right most side (first/last)
			let dropLeftSide = dropCell.first && zone.left
			let dropRightSide = dropCell.last && !zone.left
			if (dropLeftSide || dropRightSide) {
				dropNodes = nodesOfSide(zone)
				arrowNodes = dropNodes.first()
			}
		} else {
			//treat as dropping inside, highlight all the repeated nodes
			//where the drop will attach to
			dropNodes = $(dropCell.repeatedColumnDomNodes())
			arrowNodes = $(dropCell.domNodes())
		}

		this._styleDropTarget(dropNodes, position, 'vertical', arrowNodes, enter)
	}

	_styleDragOriginWithinStack(args) {
		let {dragCell, dropCell, zone, adjacentZones, enter} = args
		let draggingBreakdownRow = dragCell.stack.isRepeatByStack() && dragCell.inBreakdownRow()

		//handling drag and dropping on the same stack
		let isChangingLevel = this._isDragChangingLevel(dragCell, dropCell, zone)
		//determine the drop styles depending on the drop target
		let dropNodes, position, orientation, arrowNodes

		let targetZone = zone  //the default target it the zone passed in
		if (isChangingLevel || draggingBreakdownRow) {
			//note this will until we support manual reordering of breakdowns #25857
			dropNodes = $(dropCell.rowHeaderDomNodes())
			position = zone.top ? 'top' : 'bottom'
			arrowNodes = dropNodes.first()
			orientation = 'horizontal'
		} else {
			//we're NOT changing the level the current cell is in
			let targetCell = dropCell
			if (dragCell.position == reportConstants.REPEAT_BY_BOTTOM_POSITION) {
				//if the drop is at the bottom row, we can just use the repeated columns
				//however if the drop column is in the middle or top, we need to ignore
				//the headers in the child rows and only use the last/first cells for
				//the drop styles
				dropNodes = $(dragCell.repeatedColumnDomNodes())
			} else {
				//for middle or top row drags, if we're not changing the level then
				//we've got to determine if the drop target is in the same level
				//as the drag because the target zones to stay in the same level
				//extend beyond above and below their current row so if the
				//drag/drop row indexes don't match we need to find the
				//appropriate adjacent zone that is the actual target
				let dropRowIndex = dropCell.rowIndex()
				let dragRowIndex = dragCell.rowIndex()
				if (dropRowIndex < dragRowIndex) {
					targetZone = adjacentZones.below  //we're dropping above, the target is below
				} else if (dropRowIndex > dragRowIndex) {
					targetZone = adjacentZones.above  //we're below, target is above
				}
				targetCell = targetZone.headerCell
				dropNodes = $(targetCell.sideDomNodes(targetZone.left))
			}
			position = targetZone.left ? 'left': 'right'
			arrowNodes = $(targetCell.domNodes())
			orientation = 'vertical'
		}
		this._styleDropTarget(dropNodes, position, orientation, arrowNodes, enter)
	}

	_styleDropTarget(dropNodes, position, orientation, arrowNodes, enableStyle) {
		if (!dropNodes) { return }
		arrowNodes
			// don't want to apply this class to the invisible cell in the table
			// for the cell being dragged
			.filter(':not(.is-being-dragged)')
			.toggleClass('drop-arrow', enableStyle)
		//remove any previous drop styles
		_.each(dropNodes.currentDropStyles || [], dropNodes.removeClass)

		// #49564 always show the drop arrow at the top or bottom of the cell
		// but never both
		const isTop = position === 'top'
		if (isTop || position === 'bottom') {
			dropNodes.removeClass(isTop ? 'bottom' : 'top')
		}

		dropNodes.toggleClass(`droppable ${position} drop-line-${orientation}`, enableStyle)
		dropNodes.currentDropStyles = [position, `drop-line-${orientation}`]
	}

	/**
	 * Applies the styles to both the cells being dragged and the cells being
	 * targeted for dropping onto by dnd.  Additional emits the data to create
	 * the drag avatar.
	 *
	 * @param {HeaderCell} dragCell the cell being dragged
	 * @param {HeaderCell} dropCell the cell being targeted for dropping
	 * @Param {Object} dropZone the drop zone being targeted
	 */
	_applyDragStyle(dragCell, dropCell, dropZone) {
		let dragNodes
		let dragAvatar
		let dragType = 'horizontal-drag' // default

		let isDraggingBreakdownRow = dragCell.inBreakdownRow()
		let isDroppingInSameStack = dropCell && dropCell.tableStack === this

		//setup default avatar, which is the single column being dragged in the stack
		//see https://github.com/SpiderStrategies/Scoreboard/issues/43856#issuecomment-989100683
		dragAvatar = dragCell.stackDragAvatar(this)
		dragNodes = this.domNodes()

		$(dragNodes).toggleClass('is-being-dragged', false)
		if (isDraggingBreakdownRow) {
			if (!dropCell || isDroppingInSameStack) {
				//if we're dragging a breakdown (or default drag), the avatar is always the row itself
				//if the drop is within the stack, the nodes (hole) is the row
				dragAvatar = dragCell.rowDragAvatar()
				dragNodes = dragCell.rowHeaderDomNodes()
				dragType = 'vertical-drag'
			}
		} else {
			//we're dragging a field
			if (isDroppingInSameStack && dropCell.inBreakdownRow()
				&& this._isDragChangingLevel(dragCell, dropCell, dropZone)) {
				//we're changing levels, show a vertical drag
				dragNodes = dragCell.rowHeaderDomNodes()
				dragAvatar = dragCell.rowDragAvatar()
				dragType = 'vertical-drag'
			} else {
				//we're shifting the column position or moving out of the stack
				//show horizontal drag avatar
				dragNodes = dragCell.repeatedColumnDomNodes()
			}
		}
		// we'll pass the first node in the row to the _createAvatar() method in
		// table-dnd.js so we can use its coordinates for positioning.
		let firstDragNode = dragNodes[0]

		$(dragNodes).toggleClass('is-being-dragged', this._isDragging)
		this.emit('avatar', dragAvatar, {dragType, firstDragNode})
	}

	//special rules here when dragging non breakdown
	//https://github.com/SpiderStrategies/Scoreboard/issues/38255
	_isDragChangingLevel(dragCell, dropCell, dropZone) {
		let dragCellRow = dragCell.rowIndex()
		let dropCellRow = dropCell.rowIndex()

		if (dropCell.stack == dragCell.stack) {
			if (dragCellRow == dropCellRow) { return false }
			let isDropNextTo = dragCellRow+1 == dropCellRow || dragCellRow-1 == dropCellRow
			if (isDropNextTo) {
				let dropAbove = dragCellRow > dropCellRow
				let dropBelow = dragCellRow < dropCellRow
				let isDroppingOverTopHalf = dropAbove && dropZone.position.indexOf('top') != -1
				let isDroppingOverBottomHalf = dropBelow && dropZone.position.indexOf('bottom') != -1
				//we're in zone farthest from the field, so we're dropping in the next level
				return isDroppingOverBottomHalf || isDroppingOverTopHalf
			} else {
				//the drop target is not next to the field row, we're definitely
				//changing the level
				return true
			}
		}
		return false
	}

	_spotlightNodes() {
		let fieldRow = this.rows(this.fieldRowIndex)
		let spotlightCell = fieldRow[this.options.stack.spotlightIndex]
		return spotlightCell ? spotlightCell.repeatedColumnDomNodes() : []
	}

	dataForStackAvatar() {
		return {
			// #56490 important to use cloneDeepWithModels instead of
			// _.cloneDeep() or we end up with bad clones of Backbone Views
			columnAttributes: cloneDeepWithModels(this.columns),
			repeatByAttributes: cloneDeepWithModels(this.repeatBy),
			options: cloneDeepWithModels(this.options)
		}
	}
}

export default MultiColumnTableStack
export { determineBreakdownItemIds }
export { formatTimeValue }
MultiColumnTableStack.__filename__ = 'reports/common/table/multi-column-table-stack.js'
